import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { logout } from '../../../slices/authSlice';
import Sparkline from '../../DashboardHome/cards/Sparkline';
import Icon from '../../RushShell/icons';

const RANGES = ['Week', 'Month', 'Quarter', 'Year'];

const Reports = () => {
    const token = useSelector((s) => s.auth.token);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [stats, setStats] = useState(null);
    const [metrics, setMetrics] = useState({});
    const [range, setRange] = useState('Month');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!token) return;
        const headers = { Authorization: `Bearer ${token}` };
        Promise.all([
            axios.get('/api/stats', { headers }).then((r) => r.data).catch(() => ({})),
            axios.get(`/api/dashboard/metrics?range=${range.toLowerCase()}`, { headers }).then((r) => r.data).catch(() => ({})),
        ])
            .then(([statsRes, metricsRes]) => {
                setStats(statsRes);
                setMetrics(metricsRes);
                setLoading(false);
            })
            .catch((err) => {
                if (err.response && err.response.status === 401) {
                    dispatch(logout());
                    navigate('/login');
                } else {
                    setError('Failed to load reports.');
                    setLoading(false);
                }
            });
    }, [token, range, dispatch, navigate]);

    const wordsByDay = useMemo(() => (stats && stats.wordsByDay) || [], [stats]);
    const topLanguages = useMemo(() => (stats && stats.topLanguages) || [], [stats]);
    const maxLangValue = useMemo(() => Math.max(1, ...topLanguages.map((l) => l.count || l.words || 0)), [topLanguages]);

    if (loading) return <div style={{ padding: 40, color: 'var(--ink-3)' }}>Loading reports…</div>;
    if (error) return <div style={{ padding: 40, color: 'crimson' }}>{error}</div>;

    return (
        <>
            <div className="rs-page-title-row">
                <h1 className="rs-page-title">Reports</h1>
                <div className="rs-page-actions">
                    <div className="rs-segmented" role="tablist">
                        {RANGES.map((r) => (
                            <button key={r} type="button" className={r === range ? 'on' : ''} onClick={() => setRange(r)}>
                                {r}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
            <p className="rs-page-sub">Throughput, leverage, and language mix at a glance.</p>

            <div className="rs-metrics">
                <div className="rs-metric">
                    <div className="lbl"><span className="ic">{Icon.folder}</span><span>Active projects</span></div>
                    <div className="val">{metrics.activeProjects?.value ?? '—'}</div>
                    {metrics.activeProjects?.delta && (
                        <div className={`delta ${metrics.activeProjects.deltaDir}`}>
                            {metrics.activeProjects.deltaDir === 'up' ? '↑' : '↓'} {metrics.activeProjects.delta}
                            <span className="since">vs prior</span>
                        </div>
                    )}
                    {metrics.activeProjects?.spark && <Sparkline data={metrics.activeProjects.spark} color="var(--accent)" />}
                </div>
                <div className="rs-metric">
                    <div className="lbl"><span className="ic indigo">{Icon.globe}</span><span>Words translated</span></div>
                    <div className="val">{metrics.wordsThisMonth?.value ?? '—'}</div>
                    {metrics.wordsThisMonth?.delta && (
                        <div className={`delta ${metrics.wordsThisMonth.deltaDir}`}>
                            {metrics.wordsThisMonth.deltaDir === 'up' ? '↑' : '↓'} {metrics.wordsThisMonth.delta}
                            <span className="since">vs prior</span>
                        </div>
                    )}
                    {metrics.wordsThisMonth?.spark && <Sparkline data={metrics.wordsThisMonth.spark} color="var(--indigo)" />}
                </div>
                <div className="rs-metric">
                    <div className="lbl"><span className="ic green">{Icon.clock}</span><span>Avg turnaround</span></div>
                    <div className="val">
                        {metrics.avgTurnaround?.value ?? '—'}
                        {metrics.avgTurnaround?.unit && <span className="unit">{metrics.avgTurnaround.unit}</span>}
                    </div>
                </div>
                <div className="rs-metric">
                    <div className="lbl"><span className="ic yellow">{Icon.star}</span><span>Quality score</span></div>
                    <div className="val">
                        {metrics.qualityScore?.value ?? '—'}
                        {metrics.qualityScore?.unit && <span className="unit">{metrics.qualityScore.unit}</span>}
                    </div>
                </div>
            </div>

            <div className="rs-grid">
                <div className="rs-card col-7">
                    <div className="rs-card-h">
                        <h3>Words translated by day</h3>
                        <span className="count">{wordsByDay.length} days</span>
                    </div>
                    {wordsByDay.length === 0 ? (
                        <div style={{ padding: 24, color: 'var(--ink-3)', fontSize: 13 }}>
                            No translation activity recorded yet.
                        </div>
                    ) : (
                        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 180, padding: '12px 0' }}>
                            {wordsByDay.map((d, i) => {
                                const maxV = Math.max(1, ...wordsByDay.map((x) => x.words || 0));
                                const h = ((d.words || 0) / maxV) * 100;
                                return (
                                    <div
                                        key={i}
                                        title={`${d.date}: ${d.words} words`}
                                        style={{
                                            flex: 1,
                                            background: 'var(--accent)',
                                            height: `${h}%`,
                                            minHeight: 2,
                                            borderRadius: '4px 4px 0 0',
                                            opacity: 0.7,
                                        }}
                                    />
                                );
                            })}
                        </div>
                    )}
                </div>

                <div className="rs-card col-5">
                    <div className="rs-card-h">
                        <h3>Top target languages</h3>
                        <span className="count">{topLanguages.length}</span>
                    </div>
                    {topLanguages.length === 0 ? (
                        <div style={{ padding: 24, color: 'var(--ink-3)', fontSize: 13 }}>
                            No language data yet.
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            {topLanguages.map((l, i) => {
                                const value = l.count || l.words || 0;
                                const pct = (value / maxLangValue) * 100;
                                return (
                                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                        <span style={{
                                            minWidth: 36,
                                            fontSize: 11,
                                            fontWeight: 700,
                                            padding: '2px 6px',
                                            background: 'var(--accent-soft)',
                                            color: 'var(--accent)',
                                            borderRadius: 4,
                                            textAlign: 'center',
                                        }}>
                                            {(l.code || l.language || '').slice(0, 2).toUpperCase()}
                                        </span>
                                        <div style={{ flex: 1, background: 'var(--bg-soft)', borderRadius: 4, height: 8, overflow: 'hidden' }}>
                                            <div style={{ width: `${pct}%`, height: '100%', background: 'var(--accent)' }} />
                                        </div>
                                        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-3)', minWidth: 50, textAlign: 'right' }}>
                                            {value.toLocaleString()}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default Reports;
